import Logo, { LogoMark } from "@/components/Logo";
import { APP_LINKS, CLUB } from "@/data/content";

const SOCIAL_ICONS = {
  Instagram: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-[18px] w-[18px]">
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.2" cy="6.8" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  ),
  Facebook: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-[18px] w-[18px]">
      <path d="M15 3h-2.5A4.5 4.5 0 0 0 8 7.5V10H5v4h3v7h4v-7h3l1-4h-4V7.5c0-.6.4-1 1-1h2V3z" />
    </svg>
  ),
  TikTok: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-[18px] w-[18px]">
      <path d="M16.6 3c.4 2.2 1.8 3.6 4 3.8v2.9c-1.5 0-2.9-.5-4-1.3v5.8c0 3.7-2.7 6.3-6.2 6.3-3.3 0-5.9-2.5-5.9-5.9 0-3.3 2.6-5.9 6-5.9.4 0 .8 0 1.1.1v3.1c-.3-.1-.7-.2-1.1-.2-1.6 0-2.9 1.3-2.9 2.9s1.3 2.9 2.9 2.9c1.7 0 3-1.2 3-3.2V3h3.1z" />
    </svg>
  ),
  LinkedIn: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-[18px] w-[18px]">
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M8 10.5V17M8 7.6v.1M12 17v-3.6c0-1.6 1-2.6 2.4-2.6S17 11.8 17 13.4V17" />
    </svg>
  ),
};

function StoreButton({ label, sub, icon, href, testid }) {
  return (
    <a
      href={href}
      data-testid={testid}
      className="group inline-flex items-center gap-3 rounded-xl border border-gold/25 bg-night-soft/80 px-4 py-2.5 transition-all duration-300 hover:border-gold hover:bg-gold/10"
    >
      {icon}
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{sub}</span>
        <span className="text-sm font-bold text-foreground group-hover:text-gold">{label}</span>
      </span>
    </a>
  );
}

const AppleIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6 text-foreground">
    <path d="M16.7 12.9c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.8-3.5.8-.7 0-1.9-.8-3.1-.8-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.5.8 1.1 1.7 2.4 3 2.4 1.2-.1 1.7-.8 3.1-.8s1.9.8 3.1.8c1.3 0 2.1-1.2 2.9-2.3.9-1.3 1.3-2.6 1.3-2.7-.1 0-2.7-1-2.7-4zM14.4 5.9c.6-.8 1.1-1.9 1-3-1 0-2.1.6-2.8 1.5-.6.7-1.2 1.9-1 3 1.1 0 2.2-.7 2.8-1.5z" />
  </svg>
);

const PlayIcon = (
  <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
    <path d="M4 3.5v17c0 .4.5.7.8.4l9.1-8.5c.2-.2.2-.6 0-.8L4.8 3.1c-.3-.3-.8 0-.8.4z" fill="#D4AF37" opacity="0.9" />
    <path d="M14 8.3l3.5-2c.5-.3 1.1.1 1.1.6v10.2c0 .6-.6.9-1.1.6l-3.5-2" fill="#38BDF8" opacity="0.8" />
  </svg>
);

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="relative border-t border-gold/15 bg-night-soft" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Logo size={44} />
            <p className="mt-5 max-w-xs font-serif text-xl italic text-gold">{CLUB.baseline}</p>
            <p className="mt-3 text-sm text-muted-foreground">{CLUB.tagline}</p>
            <div className="mt-6 flex gap-3">
              {CLUB.socials.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  data-testid={`footer-social-${s.name.toLowerCase()}`}
                  aria-label={s.name}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold/25 text-gold transition-all duration-300 hover:border-gold hover:bg-gold/10 hover:-translate-y-0.5"
                >
                  {SOCIAL_ICONS[s.name]}
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            <h3 className="eyebrow mb-5">Navigation</h3>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              {[
                ["Accueil", "/"],
                ["Le Club", "/le-club"],
                ["Business Club Vaucluse", "/business-club-vaucluse"],
                ["Club Motards Vaucluse", "/club-motards-vaucluse"],
                ["Fondateurs", "/nos-fondateurs"],
                ["Application", "/application"],
                ["Adhésions", "/adhesion"],
                ["Membre Pro", "/membre-pro"],
                ["Premium", "/premium"],
                ["Partenaires", "/partenaires"],
                ["Événements", "/evenements"],
                ["FAQ", "/faq"],
                ["Blog", "/blog"],
                ["Contact", "/contact"],
                ["Mentions légales", "/mentions-legales"],
                ["Confidentialité", "/confidentialite"],
                ["CGV", "/cgv"],
                ["Cookies", "/cookies"],
              ].map(([label, to]) => (
                <li key={label}>
                  <a href={to} data-testid={`footer-link-${to.replace("/", "") || "accueil"}`} className="text-muted-foreground transition-colors hover:text-gold">
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <h3 className="eyebrow mb-5">L'application</h3>
            <p className="mb-5 text-sm text-muted-foreground">Toute la communauté, les événements et l'annuaire, dans votre poche.</p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <StoreButton label="App Store" sub="Télécharger sur" icon={AppleIcon} href={APP_LINKS.appStore} testid="footer-appstore-link" />
              <StoreButton label="Google Play" sub="Disponible sur" icon={PlayIcon} href={APP_LINKS.googlePlay} testid="footer-googleplay-link" />
            </div>
            <div className="mt-8">
              <h3 className="eyebrow mb-4">Contact</h3>
              <p className="text-sm text-muted-foreground">
                {CLUB.contact.email} — {CLUB.contact.phone}
                <br />
                {CLUB.contact.address}
                <br />
                {CLUB.contact.zone}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-gold/10 pt-8 text-xs text-muted-foreground sm:flex-row">
          <span data-testid="footer-copyright">© {year} {CLUB.name}. Tous droits réservés.</span>
          <span className="flex items-center gap-2">
            <LogoMark size={18} /> {CLUB.regions}
          </span>
        </div>
      </div>
    </footer>
  );
}
